import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { AvailabilitiesCalendarSubtype } from '@jsmdg/tracking';
import { CalendarIcon, Link as LinkAsButton, LinkVariant, RenderType } from '@jsmdg/yoshi';
import { isExperience } from '../../../shared/product-safeguards';
import { type Location } from '../../../shared/types';
import { type Availability } from '../../types/availability';
import { type ProductDetailContext } from '../../types/types';
import {
    trackCalendarAddToCartClicked,
    trackOpenCalendarClicked,
} from './availabilitiesContainer.tracking';
import styles from './AvailabilitiesContainer.module.scss';

const AvailabilityOverlay = loadable(() => import('./AvailabilityOverlay'), {
    resolveComponent: module => module.AvailabilityOverlay,
});

const messages = defineMessages({
    headline: {
        defaultMessage: 'Sieh dir die verfügbaren Termine an',
    },
    linkText: {
        defaultMessage: 'Kalender öffnen',
    },
    appointmentCount: {
        defaultMessage: '{count} {count, plural, one {Termin} other {Termine}}',
    },
    dividerText: {
        defaultMessage: 'oder',
    },
    appointmentArrangementHeadline: {
        defaultMessage: 'Verfügbare Termine',
    },
    appointmentArrangementTexts: {
        defaultMessage: 'Passenden Termin nach dem Gutscheinkauf vereinbaren.',
    },
});

type AvailabilitiesContainerProps = {
    readonly isAddToCartDisabled?: boolean;
    readonly isTicketOnly?: boolean;
    readonly enableRadicalTest?: boolean;
    readonly onAddToCartClick?: (categorySubtype?: string) => void | Promise<void>;
};

const getBookableLocations = (locations: Location[]): Location[] =>
    locations.filter(location => location.activeAppointments);

const AvailabilitiesContainer = ({
    enableRadicalTest = false,
    isAddToCartDisabled = false,
    isTicketOnly = false,
    onAddToCartClick,
}: AvailabilitiesContainerProps): JSX.Element => {
    const { handleGetAvailabilities, product } = useFragmentContext<ProductDetailContext>();
    const intl = useIntl();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [availabilitiesPromise, setAvailabilitiesPromise] = useState<Promise<
        Availability[]
    > | null>(null);

    const bookableLocations = isExperience(product) ? getBookableLocations(product.locations) : [];

    const formatAppointmentCount = (count: number): string =>
        intl.formatMessage(messages.appointmentCount, { count });

    const getAvailabilitiesForLocation = (locationId: string): Promise<Availability[]> => {
        const selectedBookableLocation = bookableLocations.find(
            location => location.externalId === locationId,
        );
        const experienceId = selectedBookableLocation?.experienceId;

        if (!experienceId) {
            return Promise.resolve([]);
        }

        return handleGetAvailabilities({
            productId: product.productId,
            experienceId,
        });
    };

    const handleOpenModal = (): void => {
        const singleBookableLocation =
            bookableLocations.length === 1 ? bookableLocations[0] : undefined;

        void trackOpenCalendarClicked();
        setIsModalOpen(true);

        if (singleBookableLocation) {
            setSelectedLocation(singleBookableLocation.externalId);
            setAvailabilitiesPromise(
                getAvailabilitiesForLocation(singleBookableLocation.externalId),
            );
        } else {
            setSelectedLocation('');
            setAvailabilitiesPromise(null);
        }
    };

    const handleLocationChange = (locationId: string): void => {
        setSelectedLocation(locationId);
        setAvailabilitiesPromise(getAvailabilitiesForLocation(locationId));
    };

    const handleAddToCart = (): void => {
        void trackCalendarAddToCartClicked();
        setIsModalOpen(false);
        void onAddToCartClick?.(AvailabilitiesCalendarSubtype.CALENDAR_VIEW);
    };

    return (
        <>
            {isTicketOnly ? (
                <div className="d-flex align-item-center gap-2x mt-2x px-2x" aria-hidden="true">
                    <span className={styles.separatorLine} />
                    <span className={styles.separatorText}>
                        {intl.formatMessage(messages.dividerText)}
                    </span>
                    <span className={styles.separatorLine} />
                </div>
            ) : (
                <div className="d-flex align-items-start gap-1x px-2x mt-3x">
                    <div
                        className={classNames(
                            styles.iconWrapper,
                            'd-flex align-items-center justify-content-center rounded-circle',
                        )}
                        aria-hidden
                    >
                        <CalendarIcon className={styles.icon} size={enableRadicalTest ? 24 : 20} />
                    </div>
                    <div className="d-flex flex-column gap-0-5x">
                        {enableRadicalTest ? (
                            <div>
                                <p className={styles.appointmentArrangementHeadline}>
                                    {intl.formatMessage(messages.appointmentArrangementHeadline)}
                                </p>
                                <p className={styles.headline}>
                                    {intl.formatMessage(messages.appointmentArrangementTexts)}
                                </p>
                            </div>
                        ) : (
                            <p className={styles.headline}>
                                {intl.formatMessage(messages.headline)}
                            </p>
                        )}

                        <LinkAsButton
                            className={classNames(
                                styles.link,
                                'd-inline-flex align-items-center gap-0-5x p-0',
                                {
                                    'd-none': enableRadicalTest,
                                },
                            )}
                            onClick={handleOpenModal}
                            variant={LinkVariant.Brand}
                            as={RenderType.Button}
                        >
                            <span>{intl.formatMessage(messages.linkText)} →</span>
                        </LinkAsButton>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <AvailabilityOverlay
                    availabilitiesPromise={availabilitiesPromise}
                    bookableLocations={bookableLocations}
                    formatAppointmentCount={formatAppointmentCount}
                    isAddToCartDisabled={isAddToCartDisabled}
                    isOpen={isModalOpen}
                    onAddToCart={handleAddToCart}
                    onLocationChange={handleLocationChange}
                    onRequestClose={() => setIsModalOpen(false)}
                    selectedLocation={selectedLocation}
                />
            )}
        </>
    );
};

export { AvailabilitiesContainer };
